import { axiosRubyClient } from '@/util/axios/axiosClient';

import { schemas } from './generated/schemas';
import {
  CreateLocationPayload,
  CreateOrderPayloadV2,
  CreateOrderResponseV2,
  CreateSavedSnippetPayload,
  CreateShiftSupervisorPayload,
  SavedSnippetFieldKind,
  WorkerCounts,
  WorkerListItem,
  WorkerSearchParams,
} from './types';

// Each function pipes the response through the matching Zod schema's
// `.parse()` so the wizard fails fast on contract drift between the
// admin_v2 OpenAPI spec and the live builder. The schemas themselves
// are generated — see `generated/schemas.ts` and `npm run openapi:generate`.
export const createOrderV2Client = {
  async listCustomersDirectory(params: {
    agencyId: number;
    customerStatus?: 'active' | 'inactive' | 'all';
    accountStatus?: 'active' | 'inactive' | 'all';
    rateType?: 'static' | 'markup' | 'all';
    fresh?: boolean;
  }) {
    const { data } = await axiosRubyClient.get(
      '/api/admin/orders_v2/customers_directory',
      {
        params: {
          agency_id: params.agencyId,
          customer_status: params.customerStatus,
          account_status: params.accountStatus,
          rate_type: params.rateType,
          fresh: params.fresh ? 1 : undefined,
        },
      },
    );
    return schemas.customers_directory_response.parse(data).data;
  },

  async listOrderTemplates(accountId: number, orderType?: string) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/order_templates`,
      { params: { order_type: orderType } },
    );
    return schemas.order_templates_response.parse(data).data;
  },

  async getOrderTemplate(templateId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/order_templates/${templateId}`,
    );
    return schemas.order_template_detail_response.parse(data).data;
  },

  async listSavedSnippets(accountId: number, fieldKind: SavedSnippetFieldKind) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/saved_snippets`,
      { params: { field_kind: fieldKind } },
    );
    return schemas.saved_snippets_list_response.parse(data).data;
  },

  async createSavedSnippet(
    accountId: number,
    payload: CreateSavedSnippetPayload,
  ) {
    const { data } = await axiosRubyClient.post(
      `/api/admin/orders_v2/billing_accounts/${accountId}/saved_snippets`,
      payload,
    );
    return schemas.saved_snippet_detail_response.parse(data).data;
  },

  async applySavedSnippet(snippetId: number) {
    const { data } = await axiosRubyClient.post(
      `/api/admin/orders_v2/saved_snippets/${snippetId}/apply`,
    );
    return schemas.saved_snippet_detail_response.parse(data).data;
  },

  async listShiftSupervisors(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/shift_supervisors`,
    );
    return schemas.shift_supervisors_response.parse(data).data;
  },

  async createShiftSupervisor(
    accountId: number,
    payload: CreateShiftSupervisorPayload,
  ) {
    const { data } = await axiosRubyClient.post(
      `/api/admin/orders_v2/billing_accounts/${accountId}/shift_supervisors`,
      payload,
    );
    return schemas.shift_supervisor_detail_response.parse(data).data;
  },

  async searchWorkers(params: WorkerSearchParams) {
    const { data } = await axiosRubyClient.get<{
      success: true;
      data: WorkerListItem[];
    }>('/api/admin/orders_v2/workers/search', { params });
    // OpenAPI documents the items as `type: object` (no inner schema),
    // so we just verify the envelope and trust the legacy serializer
    // for the per-item shape. Items are typed via `WorkerListItem`.
    schemas.workers_search_response.parse(data);
    return data.data;
  },

  async getWorkerCounts(params: WorkerSearchParams) {
    const { data } = await axiosRubyClient.get(
      '/api/admin/orders_v2/workers/counts',
      { params },
    );
    return schemas.workers_counts_response.parse(data).data as WorkerCounts;
  },

  async listLocations(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/locations`,
    );
    return schemas.locations_response.parse(data).data;
  },

  async createLocation(accountId: number, payload: CreateLocationPayload) {
    const { data } = await axiosRubyClient.post(
      `/api/admin/orders_v2/billing_accounts/${accountId}/locations`,
      payload,
    );
    return schemas.location_detail_response.parse(data).data;
  },

  async listSkills(accountId: number, search?: string) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/skills`,
      { params: search ? { search } : undefined },
    );
    return schemas.skills_response.parse(data).data;
  },

  async listSkillCategories(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/skill_categories`,
    );
    return schemas.skill_categories_response.parse(data).data;
  },

  async listUniforms(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/uniforms`,
    );
    return schemas.uniforms_response.parse(data).data;
  },

  async listPaymentMethods(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/payment_methods`,
    );
    return schemas.payment_methods_response.parse(data).data;
  },

  async listCertificates(accountId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/certificates`,
    );
    return schemas.certificates_response.parse(data).data;
  },

  async listSurgeRates(accountId: number, fromDate: string) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/surge_rates`,
      { params: { from_date: fromDate } },
    );
    return schemas.surge_rates_response.parse(data).data;
  },

  async getRateQuote(accountId: number, skillId: number, addressId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/rate_quote`,
      { params: { skill_id: skillId, address_id: addressId } },
    );
    return schemas.rate_quote_response.parse(data).data;
  },

  async getRateRange(accountId: number, skillId: number, addressId: number) {
    const { data } = await axiosRubyClient.get(
      `/api/admin/orders_v2/billing_accounts/${accountId}/rate_range`,
      { params: { skill_id: skillId, address_id: addressId } },
    );
    return schemas.rate_range_response.parse(data).data;
  },

  async createOrder(payload: CreateOrderPayloadV2) {
    const { data } = await axiosRubyClient.post<CreateOrderResponseV2>(
      '/api/admin/orders_v2/orders',
      payload,
    );
    // The error path (success: false, errors: [...]) shares this endpoint;
    // only validate the success envelope when the server reports success.
    if (data.success) schemas.order_create_response.parse(data);
    return data;
  },
};
